/**
 * Service to track highlighted items in the current view
 * This allows the StructureSidebar to display what items are highlighted
 */

import { HighlightItem } from './TextHighLightingService';

export interface TrackedHighlightItem extends HighlightItem {
  occurrences: number; // Number of times this item appears in the current view
  lastSeen: Date; // When it was last detected
}

class HighlightedItemsTrackerService {
  private trackedItems: Map<string, TrackedHighlightItem> = new Map();
  private listeners: Set<(items: TrackedHighlightItem[]) => void> = new Set();

  /**
   * Update tracked items from a view
   * @param items - Highlight items found in the current view
   */
  updateTrackedItems(items: HighlightItem[]): void {
    // Clear previous tracking
    this.trackedItems.clear();

    // Count occurrences of each item
    items.forEach(item => {
      const existing = this.trackedItems.get(item.id);
      if (existing) {
        existing.occurrences += 1;
        existing.lastSeen = new Date();
      } else {
        this.trackedItems.set(item.id, {
          ...item,
          occurrences: 1,
          lastSeen: new Date()
        });
      }
    });

    // Notify listeners
    this.notifyListeners();
  }

  /**
   * Get all tracked items
   * @returns Array of tracked items sorted by occurrences
   */
  getTrackedItems(): TrackedHighlightItem[] {
    return Array.from(this.trackedItems.values())
      .sort((a, b) => b.occurrences - a.occurrences);
  }

  /**
   * Get tracked items by type
   * @param type - Type to filter by
   * @returns Array of tracked items of that type
   */
  getTrackedItemsByType(type: string): TrackedHighlightItem[] {
    return this.getTrackedItems().filter(item => item.type === type);
  }

  /**
   * Clear all tracked items
   */
  clearTrackedItems(): void {
    this.trackedItems.clear();
    this.notifyListeners();
  }

  /**
   * Subscribe to changes in tracked items
   * @param listener - Callback function
   * @returns Unsubscribe function
   */
  subscribe(listener: (items: TrackedHighlightItem[]) => void): () => void {
    this.listeners.add(listener);
    // Immediately call with current data
    listener(this.getTrackedItems());
    
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of changes
   */
  private notifyListeners(): void {
    const items = this.getTrackedItems();
    this.listeners.forEach(listener => listener(items));
  }

  /**
   * Get statistics about tracked items
   */
  getStatistics(): {
    totalItems: number;
    totalOccurrences: number;
    byType: Record<string, number>;
  } {
    const items = this.getTrackedItems();
    const totalOccurrences = items.reduce((sum, item) => sum + item.occurrences, 0);
    const byType: Record<string, number> = {};

    items.forEach(item => {
      byType[item.type] = (byType[item.type] || 0) + 1;
    });

    return {
      totalItems: items.length,
      totalOccurrences,
      byType
    };
  }
}

// Export singleton instance
export const highlightedItemsTracker = new HighlightedItemsTrackerService();

